import { useEffect, useState } from 'react';
import { getStorageItem, setStorageItem } from 'services/storage';
import { STORAGE_KEY } from 'constants/browserStorageKeys';

const WELCOME_FORM = 'WelcomeForm';
const FEEDBACK_FORM = 'FeedbackForm';

type CurrentForm = typeof WELCOME_FORM | typeof FEEDBACK_FORM | null;

export const useForms = ({
  welcomeForm,
  feedback,
  matchFeedbackType,
  isDialogEnded,
  sendMessage,
  isLoaded,
  isFormClosed = false,
  openChat,
  closeChat,
}) => {
  const [isFormDisplayed, showForm] = useState(false);
  const [currentForm, setCurrentForm] = useState<CurrentForm>(null);

  useEffect(() => {
    if (isFormClosed) {
      resetForm();
    }
  }, [isFormClosed]);

  useEffect(() => {
    setStorageItem(STORAGE_KEY.CLOSE_FEEDBACK, false);

    const viewFeedback = getStorageItem(STORAGE_KEY.VIEW_FEEDBACK);
    const reopenFeedback = getStorageItem(STORAGE_KEY.REOPEN_FEEDBACK);
    const showWelcomeForm = getStorageItem(STORAGE_KEY.WELCOME_FORM);

    const isWelcomeForm =
      !!welcomeForm && !isLoaded && !reopenFeedback && !showWelcomeForm;
    const isFeedbackForm =
      !viewFeedback && !!feedback?.form && matchFeedbackType;

    if (isWelcomeForm) {
      showForm(true);
      setCurrentForm(WELCOME_FORM);
      setStorageItem(STORAGE_KEY.WELCOME_FORM, false);
    } else if (isFeedbackForm) {
      showForm(true);
      setCurrentForm(FEEDBACK_FORM);
      setStorageItem(STORAGE_KEY.VIEW_FEEDBACK, true);
    } else {
      resetForm();
    }
  }, [welcomeForm, isDialogEnded, isLoaded, matchFeedbackType]);

  const resetForm = () => {
    showForm(false);
    setCurrentForm(null);
  };

  const onWelcomeFormSubmit = formData => {
    setStorageItem(STORAGE_KEY.WELCOME_FORM, true);
    openChat({
      utterance: null,
      input_fields: formData,
      isFormOpen: true,
    });
    resetForm();
  };

  const onWelcomeFormSkip = () => {
    setStorageItem(STORAGE_KEY.WELCOME_FORM, true);
    openChat({
      isFormOpen: true,
    });
    resetForm();
  };

  const onFeedbackFormSubmit = formData => {
    const session_id = getStorageItem(STORAGE_KEY.SESSION_ID);
    const transfer_id = getStorageItem(STORAGE_KEY.TRANSFER_ID);
    const input_fields = {
      ...formData,
      Intent: 'provide_feedback',
      previous_dialog_id: getStorageItem(STORAGE_KEY.PREVIOUS_DIALOG_ID),
      ...(transfer_id && { transfer_id }),
    };

    if (session_id) {
      sendMessage({
        input_fields,
      });
    } else {
      openChat({
        utterance: null,
        input_fields,
        isReopen: true,
        isFormOpen: true,
      });
    }

    setStorageItem(STORAGE_KEY.VIEW_FEEDBACK, true);
    setStorageItem(STORAGE_KEY.CLOSE_FEEDBACK, true);
    resetForm();
  };

  const onFeedbackFormSkip = () => {
    getStorageItem(STORAGE_KEY.REOPEN_FEEDBACK) && closeChat();
    setStorageItem(STORAGE_KEY.VIEW_FEEDBACK, true);
    setStorageItem(STORAGE_KEY.CLOSE_FEEDBACK, true);
    resetForm();
  };

  return {
    isFormDisplayed,
    formConfig: currentForm === FEEDBACK_FORM ? feedback?.form : welcomeForm,
    onFormSubmit:
      currentForm === FEEDBACK_FORM
        ? onFeedbackFormSubmit
        : onWelcomeFormSubmit,
    onFormSkip:
      currentForm === FEEDBACK_FORM ? onFeedbackFormSkip : onWelcomeFormSkip,
  };
};
